/*
  MASTER — bloque de doble pago (COD / prepago).

  Calcula el incentivo de prepago sobre el total REAL del carrito de Shopify y
  aplica la política de acumulación. No inventa precios y no convierte divisa:
  todo sale de `cart.total_price` y se formatea con MasterMoney, así que
  funciona igual en COP, MXN, USD o EUR.

  POLÍTICA DE ACUMULACIÓN
  -----------------------
  Existen tres capas de ventaja y no todas se pueden sumar:

      pack (cantidad)  + prepago  -> SÍ
      pack (cantidad)  + rescate  -> SÍ
      prepago          + rescate  -> NO

  Las dos últimas son incentivos de recuperación sobre el mismo pedido; darlos a
  la vez regala margen dos veces por la misma conversión. Cuando ambos son
  elegibles se ofrece SÓLO el mejor, no los dos.

  Aquí se implementa el lado de PRESENTACIÓN de esa política: si el rescate ya
  está aplicado en el carrito, el incentivo de prepago deja de anunciarse (y al
  revés lo resuelve master-exit-save al leer el carrito real). La ejecución
  final la hace Shopify según cómo estén configuradas las combinaciones de los
  descuentos, que es la única autoridad transaccional.

  LÍMITE DE ENRUTADO — leer antes de tocar esto
  ---------------------------------------------
  Que el prepago cobre menos depende de que COD salga por otro camino. Ese
  contrato se decide en el snippet (`data-incentive-enforceable`) y aquí sólo se
  obedece: si no es exigible, no se pinta cifra. Si algún día se integra un
  proveedor de pago o Releasit, el punto de extensión es el destino de cada
  botón, no este cálculo.
*/
(() => {
  if (window.MasterPaymentLoaded) return;
  window.MasterPaymentLoaded = true;

  const money = cents => window.MasterMoney
    ? window.MasterMoney.format(cents)
    : String(cents);
  const t = (key, vars) => window.MasterTheme?.t?.(key, vars) ?? '';
  const truthy = value => value === true || ['true', '1', 'on', 'yes'].includes(String(value || '').toLowerCase());

  /* Misma aritmética que la ventaja por cantidad: una sola regla de redondeo en
     todo el theme, cubierta por docs/qa/offer-sim.mjs. */
  const incentiveFor = (total, type, value) => {
    const amount = Number(value) || 0;
    if (amount <= 0 || total <= 0) return 0;
    if (type === 'amount') return Math.min(total, Math.round(amount * 100));
    if (type === 'percent') return Math.min(total, Math.round(total * amount / 100));
    return 0;
  };

  /* El rescate ya cobrado se lee del carrito real, no de una bandera nuestra. */
  const rescueActive = cart => {
    const codes = window.MasterCart?.activeDiscountCodes?.(cart) || [];
    if (!codes.length) return false;
    const rescueCode = String(document.querySelector('[data-exit-save]')?.dataset.code || '').trim().toLowerCase();
    if (!rescueCode) return false;
    return codes.some(code => String(code).trim().toLowerCase() === rescueCode);
  };

  const paint = (block, cart) => {
    const total = Number(cart?.total_price || 0);
    const type = block.dataset.incentiveType || 'none';
    const enforceable = truthy(block.dataset.incentiveEnforceable);

    const codPrice = block.querySelector('[data-cod-price]');
    if (codPrice) codPrice.textContent = total > 0 ? money(total) : '';

    const saveEl = block.querySelector('[data-prepay-save]');
    const prepayPrice = block.querySelector('[data-prepay-price]');

    // Política: con el rescate ya aplicado no se anuncia también el prepago.
    const blockedByRescue = rescueActive(cart);
    const incentive = (enforceable && !blockedByRescue)
      ? incentiveFor(total, type, block.dataset.incentiveValue)
      : 0;

    block.classList.toggle('has-incentive', incentive > 0);
    block.dataset.incentiveApplied = String(incentive);

    if (saveEl) {
      saveEl.hidden = incentive <= 0;
      saveEl.textContent = incentive > 0 ? t('paymentPrepaidNote', {amount: money(incentive)}) : '';
    }
    if (prepayPrice) {
      // Sin incentivo exigible el prepago cuesta lo mismo que el COD: se muestra
      // el total tal cual en vez de esconder el precio, que sería más confuso.
      prepayPrice.textContent = total > 0 ? money(total - incentive) : '';
    }
  };

  const paintAll = cart => {
    document.querySelectorAll('[data-master-payment-choices]').forEach(block => paint(block, cart));
  };

  /* El seed del drawer evita un fetch antes del primer render. */
  const seedCart = () => {
    try {
      return JSON.parse(document.querySelector('[data-cart-seed]')?.textContent || 'null');
    } catch (_) {
      return null;
    }
  };

  let latest = seedCart();
  const refresh = () => paintAll(latest);

  document.addEventListener('master:cart-updated', event => {
    latest = event.detail?.cart || latest;
    refresh();
  });
  document.addEventListener('master:cart-section-rendered', event => {
    latest = event.detail?.cart || latest;
    refresh();
  });
  document.addEventListener('master:rescue-discount-applied', event => {
    latest = event.detail?.cart || latest;
    refresh();
  });
  document.addEventListener('shopify:section:load', refresh);

  refresh();
  if (!latest && window.MasterCart?.getCart) {
    window.MasterCart.getCart({render: false}).then(cart => { latest = cart; refresh(); }).catch(() => {});
  }

  window.MasterPayment = {incentiveFor, rescueActive, refresh};
})();
